import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Box, Button, CloseButton, createListCollection, Dialog, Field, Input, ListCollection, Portal, Select, Show, Table, VStack,} from "@chakra-ui/react"
import {useToast} from "@chakra-ui/toast";
import {CompanyExecutorFull, dataApi, ExecutorTask, Payout as Pay, PayoutList} from "../../api/data-api";
import {DateTime} from "luxon";
import {withdrawTypeList} from "../Withdraw";
import {useRecoilState} from "recoil";
import {companyData} from "../../components/hooks/recoilData";
import {errorNotify} from "../../components/hooks/CommonUtils";

export interface PayoutExt {
    id: number | null,
    userId: number | null,
    executorContractId: number | null,
    executorTaskId: number | null,
    value: string,
    fio: string,
    codeInn: string,
    additionalInfo: string,
    withdrawType: string,
    withdrawSpbTel: string,
    withdrawSpbTelBankId: string,
    withdrawCardNumber: string,
    withdrawAccountNumber: string,
    withdrawBic: string,
}

export default function Payout(props: {
    payoutListId: number | null,
    show: boolean
    setShow: React.Dispatch<React.SetStateAction<boolean>>
}) {
    const inputDefault: PayoutExt = useMemo(() => {
        return {
            id: null,
            userId: null,
            executorContractId: null,
            executorTaskId: null,
            value: "100",
            fio: "",
            codeInn: "",
            additionalInfo: "",
            withdrawType: "",
            withdrawSpbTel: "",
            withdrawSpbTelBankId: "",
            withdrawCardNumber: "",
            withdrawAccountNumber: "",
            withdrawBic: "",
        }
    }, []);
    const [inputParams, setInputParams] = useState<PayoutExt>(inputDefault);
    const [payoutList, setPayoutList] = useState<PayoutList>();
    const [items, setItems] = useState<Pay[]>([]);
    const [companyExecutors, setCompanyExecutors] = useState<CompanyExecutorFull[]>([]);
    const toast = useToast();
    const [showForm, setShowForm] = useState<boolean>(false);
    const [companyExecutorList, setCompanyExecutorList] = useState<ListCollection>(createListCollection({items: []}));
    // const [executorContractList, setExecutorContractList] = useState<ListCollection>(createListCollection({items: []}));
    const [executorTaskList, setExecutorTaskList] = useState<ListCollection>(createListCollection({items: []}));
    const [executorTasks, setExecutorTasks] = useState<ExecutorTask[]>([]);
    // const [executorContracts, setExecutorContracts] = useState<ExecutorContract[]>([]);
    const [company] = useRecoilState(companyData);

    useEffect(() => {
        if (props.payoutListId) {
            dataApi.payoutListGet(props.payoutListId)
                .then((o) => setPayoutList(o))
                .catch((err) => errorNotify(err, toast));
            dataApi.payoutLst(props.payoutListId)
                .then((o) => setItems(o))
                .catch((err) => errorNotify(err, toast));
            if (company) {
                dataApi.companyExecutorListFull(company.ID)
                    .then((o) => {
                        setCompanyExecutors(o);
                        setCompanyExecutorList(createListCollection({
                            items: o.map(it => {
                                return {label: it.FIRST_NAME + ' ' + it.SECOND_NAME + ' ' + it.LAST_NAME + ' ' + it.CODE_INN, value: String(it.USER_ID_OWN)}
                            }),
                        }));
                    })
                    .catch((err) => errorNotify(err, toast));
            }
        }
    }, [props, toast, company]);

    const prepareInput = useCallback((item: Pay | null) => {
        setInputParams({
            id: item ? item.ID : null,
            userId: item ? item.USER_ID : null,
            executorContractId: item ? item.EXECUTOR_CONTRACT_ID : null,
            executorTaskId: item ? item.EXECUTOR_TASK_ID : null,
            value: item ? String(item.VALUE1 + item.TAX) : "100",
            fio: item ? item.FIO : "",
            codeInn: item ? item.CODE_INN.trim() : "",
            additionalInfo: item ? item.ADDITIONAL_INFO : "",
            withdrawType: item ? item.WITHDRAW_TYPE1 : "",
            withdrawSpbTel: item ? item.WITHDRAW_SPB_TEL : "",
            withdrawSpbTelBankId: item ? String(item.WITHDRAW_SPB_TEL_BANK_ID) : "",
            withdrawCardNumber: item ? item.WITHDRAW_CARD_NUMBER.trim() : "",
            withdrawAccountNumber: item ? item.WITHDRAW_ACCOUNT_NUMBER.trim() : "",
            withdrawBic: item ? item.WITHDRAW_BIC.trim() : "",
        });
    }, []);

    const createOrUpdate = useCallback(() => {
        if (!props.payoutListId || !inputParams.userId || !inputParams.executorContractId || !inputParams.executorTaskId)
            return;
        dataApi.payoutCreateOrUpdate(inputParams.id, parseFloat(inputParams.value), inputParams.fio, inputParams.codeInn, inputParams.additionalInfo, inputParams.withdrawType,
            inputParams.withdrawSpbTel, parseInt(inputParams.withdrawSpbTelBankId), inputParams.withdrawCardNumber, inputParams.withdrawAccountNumber,
            inputParams.withdrawBic, props.payoutListId, inputParams.userId, inputParams.executorContractId, inputParams.executorTaskId)
            .then((value) => {
                toast({title: "Save", status: "success"});
                setItems(prev => {
                    let findO = prev.find(o => o.ID === value.ID);
                    const lst = [...prev];
                    if (findO) {
                        lst[prev.indexOf(findO)] = value;
                    } else {
                        lst.push(value);
                    }
                    return lst;
                });
                if (!inputParams.id) {
                    setInputParams(prev => {
                        return {...prev, id: value.ID};
                    });
                }
                prepareInput(null);
                setShowForm(false);
            })
            .catch((err) => errorNotify(err, toast));
    }, [inputParams, props, toast, prepareInput]);

    const payoutDelete = useCallback((it: Pay) => {
        if (!it)
            return;
        dataApi.payoutDelete(it.ID)
            .then((o) => {
                if (o) {
                    toast({title: "Delete", status: "success"});
                    setItems(prev => {
                        return prev.filter(o => o.ID !== it.ID);
                    })
                } else {
                    toast({title: "Delete failed", status: "error"});
                }
            })
            .catch((err) => errorNotify(err, toast));
    }, [toast]);

    const setInputParamUser = useCallback((str: string) => {
        if (!str)
            return;
        const userId = parseInt(str);
        setInputParams(prev => {
            return {
                ...prev, userId: userId
            }
        });
        let companyExecutor = companyExecutors.find(o => o.USER_ID_OWN === userId);
        if (!companyExecutor)
            return;

        dataApi.withdrawGet(userId)
            .then((o) => {
                setInputParams(prev => {
                    return {
                        ...prev,
                        withdrawType: o.TYPE1,
                        withdrawSpbTel: o.SPB_TEL,
                        withdrawSpbTelBankId: String(o.SPB_TEL_BANK_ID),
                        withdrawCardNumber: o.CARD_NUMBER.trim(),
                        withdrawAccountNumber: o.ACCOUNT_NUMBER.trim(),
                        withdrawBic: o.BIC,
                    }
                });
            })
            .catch((err) => errorNotify(err, toast));
        /*
                dataApi.executorContractList(companyExecutor.ID)
                    .then((o) => {
                        setExecutorContracts(o);
                                        setExecutorContractList(createListCollection({
                                            items: o.map(it => {
                                                return {label: it.NAME, value: String(it.ID)}
                                            }),
                                        }));
                    })
                    .catch((err) => errorNotify(err, toast));
        */
        setInputParams(prev => {
            return {
                ...prev,
                fio: companyExecutor ? companyExecutor.FIRST_NAME + ' ' + companyExecutor.SECOND_NAME + ' ' + companyExecutor.LAST_NAME : '',
                codeInn: companyExecutor ? companyExecutor.CODE_INN : '',
            }
        });
        // setExecutorTaskList(createListCollection({items: []}));
        dataApi.executorTaskForPayList(companyExecutor.ID)
            .then((o) => {
                setExecutorTasks(o);
                setExecutorTaskList(createListCollection({
                    items: o.map(it => {
                        return {label: it.NAME, value: String(it.ID)}
                    }),
                }));
            })
            .catch((err) => errorNotify(err, toast));

    }, [companyExecutors, toast]);

    /*
        const setInputParamContract = useCallback((executorContractId: number) => {
            setInputParams(prev => {
                return {
                    ...prev, executorContractId: executorContractId
                }
            });
            if (company) {
                dataApi.executorTaskList(executorContractId, company.ID, 0, 30)
                    .then((o) => {
                        setExecutorTasks(o);
                        setExecutorTaskList(createListCollection({
                            items: o.map(it => {
                                return {label: it.NAME, value: String(it.ID)}
                            }),
                        }));
                    })
                    .catch((err) => errorNotify(err, toast));
            }
        }, [toast, company]);
    */

    const setInputParamTask = useCallback((taskId: number) => {
        const task = executorTasks.find(o => o.ID === taskId);
        setInputParams(prev => {
            return {
                ...prev,
                executorContractId: task ? task.EXECUTOR_CONTRACT_ID : null,
                executorTaskId: task ? task.ID : null,
                value: task ? String(task.RESULT_VALUE ? task.RESULT_VALUE : task.VALUE1) : "1"
            }
        });
    }, [executorTasks]);

    return (
        <Dialog.Root size={"xl"} open={props.show} onOpenChange={(v: any) => props.setShow(v.open)}>
            <Dialog.Trigger/>
            <Portal>
                <Dialog.Backdrop/>
                <Dialog.Positioner>
                    <Dialog.Content>
                        <Dialog.Header>
                            <Dialog.Title>Выплаты</Dialog.Title>
                        </Dialog.Header>
                        <Dialog.Body>
                            <VStack>
                                <Button onClick={e => {
                                    if (showForm) {
                                        setShowForm(false);
                                    } else {
                                        prepareInput(null);
                                        setShowForm(true);
                                    }
                                }}>
                                    Создать новый
                                </Button>
                                <Show when={showForm}>
                                    <Box borderWidth={1} borderColor="gray">
                                        <Field.Root orientation="horizontal" required>
                                            <Field.Label>Исполнитель <Field.RequiredIndicator/></Field.Label>
                                            <Select.Root
                                                value={[String(inputParams.userId)]}
                                                // defaultValue={[String(inputParams.userId)]}
                                                collection={companyExecutorList}
                                                onValueChange={(e: any) => setInputParamUser(e.value[0])}>
                                                <Select.HiddenSelect/>
                                                <Select.Control>
                                                    <Select.Trigger>
                                                        <Select.ValueText placeholder="Исполнитель"/>
                                                    </Select.Trigger>
                                                    <Select.IndicatorGroup>
                                                        <Select.Indicator/>
                                                    </Select.IndicatorGroup>
                                                </Select.Control>
                                                <Select.Positioner>
                                                    <Select.Content>
                                                        {companyExecutorList.items.map((it: any) => (
                                                            <Select.Item item={it} key={it.value}>
                                                                {it.label}
                                                                <Select.ItemIndicator/>
                                                            </Select.Item>
                                                        ))}
                                                    </Select.Content>
                                                </Select.Positioner>
                                            </Select.Root>
                                        </Field.Root>
                                        {/*
                                        <Field.Root orientation="horizontal" required>
                                            <Field.Label>Контракт <Field.RequiredIndicator/></Field.Label>
                                            <Select.Root
                                                value={[String(inputParams.executorContractId)]}
                                                // defaultValue={[String(inputParams.executorContractId)]}
                                                collection={executorContractList}
                                                onValueChange={(e: any) => setInputParamContract(parseInt(e.value[0]))}>
                                                <Select.HiddenSelect/>
                                                <Select.Control>
                                                    <Select.Trigger>
                                                        <Select.ValueText placeholder="Контракт"/>
                                                    </Select.Trigger>
                                                    <Select.IndicatorGroup>
                                                        <Select.Indicator/>
                                                    </Select.IndicatorGroup>
                                                </Select.Control>
                                                <Select.Positioner>
                                                    <Select.Content>
                                                        {executorContractList.items.map((it: any) => (
                                                            <Select.Item item={it} key={it.value}>
                                                                {it.label}
                                                                <Select.ItemIndicator/>
                                                            </Select.Item>
                                                        ))}
                                                    </Select.Content>
                                                </Select.Positioner>
                                            </Select.Root>
                                        </Field.Root>
*/}
                                        <Field.Root orientation="horizontal" required>
                                            <Field.Label>Задача <Field.RequiredIndicator/></Field.Label>
                                            <Select.Root
                                                value={[String(inputParams.executorTaskId)]}
                                                // defaultValue={[String(inputParams.executorTaskId)]}
                                                collection={executorTaskList}
                                                onValueChange={(e: any) => setInputParamTask(parseInt(e.value[0]))}>
                                                <Select.HiddenSelect/>
                                                <Select.Control>
                                                    <Select.Trigger>
                                                        <Select.ValueText placeholder="Задача"/>
                                                    </Select.Trigger>
                                                    <Select.IndicatorGroup>
                                                        <Select.Indicator/>
                                                    </Select.IndicatorGroup>
                                                </Select.Control>
                                                <Select.Positioner>
                                                    <Select.Content>
                                                        {executorTaskList.items.map((it: any) => (
                                                            <Select.Item item={it} key={it.value}>
                                                                {it.label}
                                                                <Select.ItemIndicator/>
                                                            </Select.Item>
                                                        ))}
                                                    </Select.Content>
                                                </Select.Positioner>
                                            </Select.Root>
                                        </Field.Root>
                                        <Field.Root orientation="horizontal" required>
                                            <Field.Label>Сумма <Field.RequiredIndicator/></Field.Label>
                                            <Input type="number" placeholder="Сумма"
                                                   value={inputParams.value}
                                                   onChange={(it) => setInputParams((prevState) => {
                                                       return {...prevState, value: it.target.value}
                                                   })}/>
                                        </Field.Root>
                                        <Field.Root orientation="horizontal">
                                            <Field.Label>ФИО</Field.Label>
                                            <Input type="text" disabled value={inputParams.fio}/>
                                        </Field.Root>
                                        <Field.Root orientation="horizontal">
                                            <Field.Label>ИНН</Field.Label>
                                            <Input type="text" disabled value={inputParams.codeInn}/>
                                        </Field.Root>
                                        <Field.Root orientation="horizontal">
                                            <Field.Label>Дополнительная информация</Field.Label>
                                            <Input type="text" placeholder="additionalInfo"
                                                   value={inputParams.additionalInfo}
                                                   onChange={(it) => setInputParams((prevState) => {
                                                       return {...prevState, additionalInfo: it.target.value}
                                                   })}/>
                                        </Field.Root>
                                        <Field.Root orientation="horizontal" required>
                                            <Field.Label>Тип вывода <Field.RequiredIndicator/></Field.Label>
                                            <Select.Root
                                                value={[String(inputParams.withdrawType)]}
                                                // defaultValue={[String(inputParams.withdrawType)]}
                                                collection={withdrawTypeList}
                                                onValueChange={(e: any) => setInputParams(prev => {
                                                    return {
                                                        ...prev, withdrawType: e.value[0]
                                                    }
                                                })}>
                                                <Select.HiddenSelect/>
                                                <Select.Control>
                                                    <Select.Trigger>
                                                        <Select.ValueText placeholder="Тип вывода"/>
                                                    </Select.Trigger>
                                                    <Select.IndicatorGroup>
                                                        <Select.Indicator/>
                                                    </Select.IndicatorGroup>
                                                </Select.Control>
                                                <Select.Positioner>
                                                    <Select.Content>
                                                        {withdrawTypeList.items.map((it: any) => (
                                                            <Select.Item item={it} key={it.value}>
                                                                {it.label}
                                                                <Select.ItemIndicator/>
                                                            </Select.Item>
                                                        ))}
                                                    </Select.Content>
                                                </Select.Positioner>
                                            </Select.Root>
                                        </Field.Root>
                                        <Show when={inputParams.withdrawType === "TEL"}>
                                            <Field.Root orientation="horizontal">
                                                <Field.Label>Телефон</Field.Label>
                                                <Input type="text" placeholder="Телефон"
                                                       value={inputParams.withdrawSpbTel}
                                                       onChange={(it) => setInputParams((prevState) => {
                                                           return {...prevState, withdrawSpbTel: it.target.value}
                                                       })}/>
                                            </Field.Root>
                                            <Field.Root orientation="horizontal">
                                                <Field.Label>Банк</Field.Label>
                                                <Input type="text" placeholder="Банк"
                                                       value={inputParams.withdrawSpbTelBankId}
                                                       onChange={(it) => setInputParams((prevState) => {
                                                           return {...prevState, withdrawSpbTelBankId: it.target.value}
                                                       })}/>
                                            </Field.Root>
                                        </Show>
                                        <Show when={inputParams.withdrawType === "CARD"}>
                                            <Field.Root orientation="horizontal">
                                                <Field.Label>Номер карты</Field.Label>
                                                <Input type="text" placeholder="Номер карты"
                                                       value={inputParams.withdrawCardNumber}
                                                       onChange={(it) => setInputParams((prevState) => {
                                                           return {...prevState, withdrawCardNumber: it.target.value}
                                                       })}/>
                                            </Field.Root>
                                        </Show>
                                        <Show when={inputParams.withdrawType === "ACC"}>
                                            <Field.Root orientation="horizontal">
                                                <Field.Label>Банковский счет</Field.Label>
                                                <Input type="text" placeholder="Банковский счет"
                                                       value={inputParams.withdrawAccountNumber}
                                                       onChange={(it) => setInputParams((prevState) => {
                                                           return {...prevState, withdrawAccountNumber: it.target.value}
                                                       })}/>
                                            </Field.Root>
                                            <Field.Root orientation="horizontal">
                                                <Field.Label>БИК</Field.Label>
                                                <Input type="text" placeholder="БИК"
                                                       value={inputParams.withdrawBic}
                                                       onChange={(it) => setInputParams((prevState) => {
                                                           return {...prevState, withdrawBic: it.target.value}
                                                       })}/>
                                            </Field.Root>
                                        </Show>
                                        <Button borderRadius={0} variant="solid" colorScheme="teal" width="full"
                                                onClick={e => createOrUpdate()}>
                                            {inputParams.id ? "Сохранить" : "Создать"}
                                        </Button>
                                    </Box>
                                </Show>
                                <Table.Root size="sm" variant="outline" striped>
                                    <Table.Header>
                                        <Table.Row>
                                            <Table.ColumnHeader>ФИО</Table.ColumnHeader>
                                            <Table.ColumnHeader>Сумма</Table.ColumnHeader>
                                            <Table.ColumnHeader>Налог</Table.ColumnHeader>
                                            <Table.ColumnHeader>Комиссия</Table.ColumnHeader>
                                            <Table.ColumnHeader>Создан</Table.ColumnHeader>
                                            <Table.ColumnHeader>Изменен</Table.ColumnHeader>
                                            <Table.ColumnHeader>Отправлен</Table.ColumnHeader>
                                            <Table.ColumnHeader>Обработан</Table.ColumnHeader>
                                            <Table.ColumnHeader>Отменен</Table.ColumnHeader>
                                            <Table.ColumnHeader>Налог оплачен</Table.ColumnHeader>
                                            <Table.ColumnHeader>Действия</Table.ColumnHeader>
                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                        {items.map((item) => (
                                            <Table.Row key={item.ID}>
                                                <Table.Cell>{item.FIO}</Table.Cell>
                                                <Table.Cell>{item.VALUE1}</Table.Cell>
                                                <Table.Cell>{item.TAX}</Table.Cell>
                                                <Table.Cell>{item.COMMISSION}</Table.Cell>
                                                <Table.Cell>{item.CREATED ? DateTime.fromMillis(item.CREATED).toFormat('HH:mm:ss dd.MM.yyyy') : ""}</Table.Cell>
                                                <Table.Cell>{item.VERSION ? DateTime.fromMillis(item.VERSION).toFormat('HH:mm:ss dd.MM.yyyy') : ""}</Table.Cell>
                                                <Table.Cell>{item.SENDED ? DateTime.fromMillis(item.SENDED).toFormat('HH:mm:ss dd.MM.yyyy') : ""}</Table.Cell>
                                                <Table.Cell>{item.PROCESSED ? DateTime.fromMillis(item.PROCESSED).toFormat('HH:mm:ss dd.MM.yyyy') : ""}</Table.Cell>
                                                <Table.Cell>{item.REJECTED ? DateTime.fromMillis(item.REJECTED).toFormat('HH:mm:ss dd.MM.yyyy') : ""}</Table.Cell>
                                                <Table.Cell>{item.TAX_PAID ? DateTime.fromMillis(item.TAX_PAID).toFormat('HH:mm:ss dd.MM.yyyy') : ""}</Table.Cell>
                                                <Table.Cell>
                                                    <Show
                                                        when={payoutList && !payoutList.DOCUMENT_ID && !item.DOCUMENT_ID && !item.SENDED && !item.PROCESSED && !item.TAX_PAID}>
                                                        <Button onClick={e => {
                                                            if (showForm) {
                                                                setShowForm(false);
                                                            } else {
                                                                prepareInput(item);
                                                                setShowForm(true);
                                                            }
                                                        }}>Редактировать</Button>
                                                        <Button onClick={e => payoutDelete(item)}>Удалить</Button>
                                                    </Show>
                                                </Table.Cell>
                                            </Table.Row>
                                        ))}
                                    </Table.Body>
                                </Table.Root>
                            </VStack>
                        </Dialog.Body>
                        <Dialog.Footer/>
                        <Dialog.CloseTrigger asChild>
                            <CloseButton size="sm" onClick={e => props.setShow(false)}/>
                        </Dialog.CloseTrigger>
                    </Dialog.Content>
                </Dialog.Positioner>
            </Portal>
        </Dialog.Root>
    );
}